import { useEffect } from 'react';
import { runBackHandler, clickVisibleBackButton } from '../utils/backStack';

/**
 * Wire Android hardware-back / gesture-back to traverse the web-app
 * navigation stack (React Router + `useHistoryClose` sentinels) instead
 * of exiting the APK immediately.
 *
 * Capacitor 5+ removed the default WebView back-button hook, so without
 * `@capacitor/app` a physical back press finishes the Activity right
 * away.  We restore Instagram-style behaviour:
 *
 *   D → back → C → back → B → back → A → back → exitApp()
 *
 * On plain web (browser preview), this hook is a no-op — the browser's
 * native back already does the right thing.
 */
export const useAndroidBackButton = () => {
  useEffect(() => {
    let removeListener = null;
    let cancelled = false;

    (async () => {
      let AppPlugin;
      try {
        // Dynamic import so web builds never bundle native shim
        AppPlugin = (await import('@capacitor/app')).App;
      } catch {
        return; // Plugin not present — silently no-op on pure web
      }

      // Only wire on native platforms
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor?.isNativePlatform?.()) return;
      } catch {
        return;
      }

      if (cancelled) return;

      // Modal sentinels are handled by the back stack on native — ignore them.
      const origPush = window.history.pushState.bind(window.history);
      window.history.pushState = (state, ...rest) => {
        if (state && state.__modal) return;
        origPush(state, ...rest);
      };

      const handle = await AppPlugin.addListener('backButton', ({ canGoBack }) => {
        // 1) the newest open screen / sheet closes or steps back
        if (runBackHandler()) return;
        // 2) an on-screen back button of the current page / sheet
        if (clickVisibleBackButton()) return;
        // 3) otherwise walk the route history
        if (canGoBack) {
          window.history.back();
        } else {
          // No more history — minimise the app (Android convention) so
          // the user can still resume state where they left off.
          try {
            AppPlugin.minimizeApp?.();
          } catch {
            AppPlugin.exitApp?.();
          }
        }
      });

      removeListener = () => handle.remove();
    })();

    return () => {
      cancelled = true;
      if (removeListener) removeListener();
    };
  }, []);
};

export default useAndroidBackButton;
